import { useState, type FormEvent } from "react";

const FORM_ADDR = "https://readdy.ai/api/form/da2jkobd3r93mn1k3a70";

export default function Contact() {
  const [status, setStatus] = useState<{ type: "idle" | "ok" | "error"; msg: string }>({
    type: "idle",
    msg: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    // 蜜罐反垃圾：命中则不提交，仅给出常规反馈
    const honeypot = String(formData.get("phone_alt") || "").trim();
    if (honeypot) {
      setStatus({ type: "ok", msg: "感谢你的来信，我们会尽快回复。" });
      return;
    }

    setSubmitting(true);
    setStatus({ type: "idle", msg: "" });
    try {
      const payload = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key === "phone_alt") return;
        payload.append(key, String(value));
      });

      const res = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: Record<string, unknown> | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const meta = (parsed?.meta as Record<string, unknown>) || {};
      const serverMsg =
        (meta?.message as string) || (parsed?.message as string) || (meta?.detail as string) || "";

      const codeOk = parsed?.code === "OK";
      if (res.ok && codeOk) {
        setStatus({ type: "ok", msg: "感谢你的来信，我们会尽快回复你。" });
        form.reset();
      } else {
        setStatus({
          type: "error",
          msg: serverMsg || "提交失败，请稍后再试。",
        });
      }
    } catch {
      setStatus({ type: "error", msg: "网络异常，请稍后再试。" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="w-full bg-background-50 py-16 md:py-24">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6">
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-2">
          <div>
            <span className="text-sm font-medium tracking-wide text-primary-600">CONTACT</span>
            <h2 className="font-heading mt-2 text-3xl text-foreground-950 md:text-4xl">联系我们</h2>
            <p className="mt-4 text-base leading-relaxed text-foreground-600">
              有任何剧情疑问、纠错建议，或想加入我们一起整理《甜蜜惩罚》的追更内容，
              欢迎留言给我们。我们会认真阅读每一封来信。
            </p>

            <div className="mt-8 flex flex-col gap-5">
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-primary-100 text-xl text-primary-600">
                  <i className="ri-mail-line" />
                </span>
                <div>
                  <div className="text-sm text-foreground-500">联系邮箱</div>
                  <div className="text-foreground-900">contact@readdy.site</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-accent-100 text-xl text-accent-900">
                  <i className="ri-chat-3-line" />
                </span>
                <div>
                  <div className="text-sm text-foreground-500">追更交流</div>
                  <div className="text-foreground-900">欢迎在评论区与读者一起探讨结局走向</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-secondary-100 text-xl text-secondary-900">
                  <i className="ri-tools-line" />
                </span>
                <div>
                  <div className="text-sm text-foreground-500">纠错反馈</div>
                  <div className="text-foreground-900">发现剧情梳理有误？告诉我们尽快修正</div>
                </div>
              </div>
            </div>
          </div>

          <form
            data-readdy-form
            onSubmit={handleSubmit}
            className="flex flex-col gap-4 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8"
          >
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div className="flex flex-col gap-1.5">
                <label htmlFor="name" className="text-sm font-medium text-foreground-800">你的称呼</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  required
                  placeholder="如何称呼你"
                  className="rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <label htmlFor="email" className="text-sm font-medium text-foreground-800">联系邮箱</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder="name@example.com"
                  className="rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
                />
              </div>
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="subject" className="text-sm font-medium text-foreground-800">留言主题</label>
              <input
                id="subject"
                name="subject"
                type="text"
                placeholder="剧情疑问 / 纠错建议 / 追更共创"
                className="rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label htmlFor="message" className="text-sm font-medium text-foreground-800">留言内容</label>
              <textarea
                id="message"
                name="message"
                required
                maxLength={500}
                rows={5}
                placeholder="写下你想说的话…"
                className="rounded-md border border-background-200 bg-background-50 px-4 py-2.5 text-sm text-foreground-900 outline-none transition-colors placeholder:text-foreground-400 focus:border-primary-400"
              />
              <span className="text-right text-xs text-foreground-400">最多 500 字</span>
            </div>
            {/* 反垃圾蜜罐字段 */}
            <input
              name="phone_alt"
              type="text"
              tabIndex={-1}
              autoComplete="off"
              aria-hidden="true"
              readOnly
            />
            {status.type === "ok" && (
              <p className="rounded-md bg-secondary-100 px-4 py-3 text-sm text-secondary-900">{status.msg}</p>
            )}
            {status.type === "error" && (
              <p className="rounded-md bg-primary-100 px-4 py-3 text-sm text-primary-700">{status.msg}</p>
            )}
            <button
              type="submit"
              disabled={submitting}
              className="whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting ? "发送中…" : "发送留言"}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
}